import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, AngularSvgIconModule, TranslateModule],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss'
})
export class DatePickerComponent implements OnChanges {
  @Input() value: string = '';
  @Input() placeholder: string = 'Select date';
  @Input() disablePastDates: boolean = false;
  @Output() valueChange = new EventEmitter<string>();

  show = false;
  pickerYear: number = new Date().getFullYear();
  pickerMonth: number = new Date().getMonth();

  readonly weekDays = ['DATEPICKER_WD_MO', 'DATEPICKER_WD_TU', 'DATEPICKER_WD_WE', 'DATEPICKER_WD_TH', 'DATEPICKER_WD_FR', 'DATEPICKER_WD_SA', 'DATEPICKER_WD_SU'];
  readonly monthNames = ['DATEPICKER_MONTH_JAN', 'DATEPICKER_MONTH_FEB', 'DATEPICKER_MONTH_MAR', 'DATEPICKER_MONTH_APR', 'DATEPICKER_MONTH_MAY', 'DATEPICKER_MONTH_JUN', 'DATEPICKER_MONTH_JUL', 'DATEPICKER_MONTH_AUG', 'DATEPICKER_MONTH_SEP', 'DATEPICKER_MONTH_OCT', 'DATEPICKER_MONTH_NOV', 'DATEPICKER_MONTH_DEC'];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']?.currentValue) {
      const [y, m] = (changes['value'].currentValue as string).split('-').map(Number);
      this.pickerYear = y;
      this.pickerMonth = m - 1;
    }
  }

  get formattedValue(): string {
    if (!this.value) return '';
    const [y, m, d] = this.value.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  }

  get calendarDays(): Array<{ day: number; currentMonth: boolean; dateStr: string }> {
    const year = this.pickerYear;
    const month = this.pickerMonth;
    const firstDay = new Date(year, month, 1).getDay();
    const firstDayMon = firstDay === 0 ? 6 : firstDay - 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const cells: Array<{ day: number; currentMonth: boolean; dateStr: string }> = [];

    for (let i = firstDayMon - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const pm = month === 0 ? 11 : month - 1;
      const py = month === 0 ? year - 1 : year;
      cells.push({ day: d, currentMonth: false, dateStr: `${py}-${String(pm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, currentMonth: true, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
    }

    const rem = 42 - cells.length;
    const nm = month === 11 ? 0 : month + 1;
    const ny = month === 11 ? year + 1 : year;
    for (let d = 1; d <= rem; d++) {
      cells.push({ day: d, currentMonth: false, dateStr: `${ny}-${String(nm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
    }

    return cells;
  }

  get yearOptions(): number[] {
    const cur = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => cur - 2 + i);
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.show = false;
  }

  toggle(event: MouseEvent) {
    event.stopPropagation();
    this.show = !this.show;
  }

  stopPropagation(event: MouseEvent) {
    event.stopPropagation();
  }

  prevMonth() {
    if (this.pickerMonth === 0) { this.pickerMonth = 11; this.pickerYear--; }
    else this.pickerMonth--;
  }

  nextMonth() {
    if (this.pickerMonth === 11) { this.pickerMonth = 0; this.pickerYear++; }
    else this.pickerMonth++;
  }

  isPastDate(dateStr: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d) < today;
  }

  selectDate(dateStr: string) {
    if (this.disablePastDates && this.isPastDate(dateStr)) return;
    this.valueChange.emit(dateStr);
    this.show = false;
  }
}
